/**
 * Centralized API Service with request deduplication, caching, and error handling.
 * Eliminates duplicate API calls across components.
 */

const API_URL = process.env.REACT_APP_API_URL || 'https://api2b.pro';

// ── In-memory request cache (deduplicates in-flight and completed requests) ──
const cache = new Map();
const pendingRequests = new Map();
const CACHE_TTL = 30_000; // 30 seconds default TTL

function getCacheKey(url, options = {}) {
  const body = options.body || '';
  const method = options.method || 'GET';
  return `${method}:${url}:${body}`;
}

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/**
 * Core fetch wrapper with:
 * - Auto auth header injection
 * - Request deduplication (concurrent identical requests share one promise)
 * - Response caching with configurable TTL
 * - Status checking (throws on non-2xx)
 * - AbortController support
 */
async function apiFetch(url, options = {}) {
  const { useCache = true, cacheTTL = CACHE_TTL, signal, ...fetchOptions } = options;

  const resolvedUrl = url.startsWith('http') ? url : `${API_URL}${url}`;
  const cacheKey = getCacheKey(resolvedUrl, fetchOptions);

  // Return cached response if still valid
  if (useCache && cache.has(cacheKey)) {
    const cached = cache.get(cacheKey);
    if (Date.now() - cached.timestamp < cacheTTL) {
      return cached.data;
    }
    cache.delete(cacheKey);
  }

  // Deduplicate in-flight requests
  if (pendingRequests.has(cacheKey)) {
    return pendingRequests.get(cacheKey);
  }

  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
    ...(fetchOptions.headers || {}),
  };

  const requestPromise = (async () => {
    try {
      const response = await fetch(resolvedUrl, {
        ...fetchOptions,
        headers,
        signal,
      });

      if (!response.ok) {
        // Try to parse error body
        let errorData;
        try {
          errorData = await response.json();
        } catch {
          errorData = { message: response.statusText };
        }
        const error = new Error(errorData.message || `HTTP ${response.status}`);
        error.status = response.status;
        error.data = errorData;
        throw error;
      }

      // Determine response type
      const contentType = response.headers.get('content-type') || '';
      let data;
      if (contentType.includes('application/json')) {
        data = await response.json();
      } else if (contentType.includes('text/')) {
        data = await response.text();
      } else {
        data = await response.blob();
      }

      // Cache successful responses
      if (useCache) {
        cache.set(cacheKey, { data, timestamp: Date.now() });
      }

      return data;
    } finally {
      pendingRequests.delete(cacheKey);
    }
  })();

  pendingRequests.set(cacheKey, requestPromise);
  return requestPromise;
}

/**
 * Public API: GET request
 */
export function apiGet(url, options = {}) {
  return apiFetch(url, { ...options, method: 'GET' });
}

/**
 * Public API: POST request
 */
export function apiPost(url, body = {}, options = {}) {
  return apiFetch(url, {
    ...options,
    method: 'POST',
    body: JSON.stringify(body),
    useCache: false, // Don't cache POST by default
  });
}

/**
 * Public API: PATCH request
 */
export function apiPatch(url, body = {}, options = {}) {
  return apiFetch(url, {
    ...options,
    method: 'PATCH',
    body: JSON.stringify(body),
    useCache: false,
  });
}

/**
 * Public API: DELETE request
 */
export function apiDelete(url, options = {}) {
  return apiFetch(url, { ...options, method: 'DELETE', useCache: false });
}

/**
 * Invalidate a specific cache entry or entire cache
 */
export function invalidateCache(urlPattern = null) {
  if (urlPattern) {
    for (const key of cache.keys()) {
      if (key.includes(urlPattern)) {
        cache.delete(key);
      }
    }
  } else {
    cache.clear();
  }
}

/**
 * Prefetch and cache a URL (useful for eager loading on hover/predictable navigation)
 */
export function prefetch(url) {
  return apiGet(url).catch(() => {}); // Silent catch for prefetch
}

// ── Convenience methods for commonly duplicated endpoints ──

let logoPromise = null;
export function fetchLogo(originOverride) {
  const origin = originOverride || window.location.hostname.replace(/^www\./, '');
  const cacheKey = `logo_${origin}`;

  // Return cached promise to deduplicate across components
  if (logoPromise) {
    return logoPromise;
  }

  logoPromise = apiGet(`/api/logo?origin=${encodeURIComponent(origin)}`, {
    cacheTTL: 300_000, // 5 min cache for logo
  }).finally(() => {
    // Reset after 5 min so subsequent calls re-fetch
    setTimeout(() => { logoPromise = null; }, 300_000);
  });

  return logoPromise;
}

let generalPromise = null;
export function fetchGeneral() {
  if (generalPromise) return generalPromise;

  generalPromise = apiGet('/api/general', { cacheTTL: 120_000 })
    .finally(() => {
      setTimeout(() => { generalPromise = null; }, 120_000);
    });

  return generalPromise;
}

export default apiFetch;
